import React, { useRef, useEffect } from 'react';
import CurrentTimeline from './current-timeline';
import { formatDateInTimeZone, formatTimeInTimeZone } from '../../utils/timezone';
import {observer} from "mobx-react-lite";
import {useCalendarViewModel} from "../../view-model/professional/calendar-view-model";
import {calendarStore} from "../../store/calendar-store";
import {t} from "i18next";
import { Edit3 } from 'lucide-react';


const CalendarWeekView: React.FC = observer(() => {
  const calendarViewModel = useCalendarViewModel()
  const calendarRef = useRef<HTMLDivElement>(null);

  // Faire défiler jusqu'à l'heure actuelle au chargement
  useEffect(() => {
    if (calendarRef.current) {
      const now = new Date();
      const currentHour = now.getHours();
      
      // Trouver l'élément d'heure le plus proche
      const hourId = `hour-${currentHour}`;
      const hourElement = document.getElementById(hourId);
      
      if (hourElement) {
        // Défiler jusqu'à l'heure actuelle avec un léger décalage pour voir les heures précédentes
        setTimeout(() => {
          hourElement.scrollIntoView({ block: 'center' });
        }, 100);
      }
    }
  }, []);
  

  
  return (
    <div className="h-full" ref={calendarRef}>
      {/* En-tête des jours de la semaine */}
      <div className="grid grid-cols-7 md:grid-cols-7 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 sticky top-0 z-10 overflow-x-auto mb-4">
        {calendarViewModel.getDaysOfWeek.map((dayDate: Date, index: number) => {
          const isCurrentDay = calendarViewModel.isToday(dayDate);
          
          return (
            <div 
              key={index} 
              className={`p-2 text-center ${isCurrentDay ? 'font-bold text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}
            >
              <div className="text-xs uppercase">{formatDateInTimeZone(dayDate, 'EEE')}</div>
              <div className={`text-xl mt-1 ${isCurrentDay ? 'h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto' : ''}`}>
                {formatDateInTimeZone(dayDate, 'd')}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Grille du calendrier */}
      <div className="relative overflow-x-auto pl-12 pr-2 pt-2" id="calendar-grid">
        {/* Ligne indiquant l'heure actuelle */}
        <CurrentTimeline />
        {/* Lignes d'heures */}
        {calendarViewModel.generateTimeSlots.map((timeSlot: string, hourIndex: number) => {
          // N'afficher que les heures pleines pour les lignes
          if (!timeSlot.endsWith(':00')) return null;
          
          const [hour] = timeSlot.split(':').map(Number);
          
          return (
            <div 
              key={hourIndex} 
              id={`hour-${hour}`}
              className="grid grid-cols-7 md:grid-cols-7 border-b border-gray-200 dark:border-gray-700">
              {/* Indicateur d'heure */}
              <div className="absolute -left-0 text-xs font-medium text-gray-500 dark:text-gray-400 mt-2 w-10 text-right pr-2">
                {timeSlot}
              </div>
              
              {/* Cellules pour chaque jour */}
              {calendarViewModel.getDaysOfWeek.map((dayDate: Date, dayIndex: number) => {
                // Filtrer les rendez-vous qui commencent à cette heure et ce jour
                const hourAppointments = calendarStore.calendarEvents.filter(appointment => {
                  const appointmentDate = new Date(appointment.start);
                  
                  // Vérifier si le rendez-vous est pour ce jour et commence à cette heure exactement
                  const appointmentDateStr = formatDateInTimeZone(appointmentDate, 'yyyy-MM-dd');
                  const dayDateStr = formatDateInTimeZone(dayDate, 'yyyy-MM-dd');
                  const appointmentHour = parseInt(formatTimeInTimeZone(appointmentDate, 'HH'));
                  
                  return appointmentDateStr === dayDateStr && appointmentHour === hour;
                });
                
                return (
                  <div 
                    key={dayIndex} 
                    className="h-16 border-r border-gray-200 dark:border-gray-700 relative group"
                  >
                    {/* Fond de la cellule avec effet hover */}
                    <div className="absolute inset-0 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"></div>
                    
                    {/* Rendez-vous */}
                    <div className="absolute inset-0 p-1 overflow-visible">
                      {hourAppointments.map(appointment => {
                        // Calculer la hauteur en fonction de la durée
                        const durationMinutes = (appointment.end.getTime() - appointment.start.getTime()) / (1000 * 60);
                        
                        // Calculer la hauteur en pourcentage de la cellule
                        // Pour un rendez-vous qui dure plus d'une heure, on utilise un pourcentage qui dépasse 100%
                        const durationHours = durationMinutes / 60;
                        
                        // La hauteur d'une cellule est de 64px (h-16), donc on calcule le nombre de cellules à couvrir
                        const cellsToSpan = Math.ceil(durationHours);
                        
                        // On calcule la hauteur en pourcentage de la cellule initiale, plus les cellules suivantes si nécessaire
                        // 100% = 1 heure, 200% = 2 heures, etc.
                        const heightPercentage = (durationMinutes / 60) * 100;
                        
                        // Position en pourcentage basée sur les minutes
                        const topPosition = (appointment.start.getMinutes() / 60) * 100;
                        
                        return (
                          <div 
                            key={appointment.id} 
                            className="absolute left-1 right-1 rounded-md text-white text-xs shadow-sm overflow-hidden cursor-pointer transition-all hover:shadow-md hover:scale-[1.02]"
                            style={{
                              backgroundColor: appointment.color,
                              top: `${topPosition}%`,
                              height: `${heightPercentage}%`,
                              minHeight: '20px',
                              maxHeight: durationHours > 1 ? `${cellsToSpan * 64}px` : 'none',
                              zIndex: durationHours > 1 ? 10 : 1,
                              transition: 'all 0.2s ease'
                            }}
                            onClick={() => calendarViewModel.openEventDetails(appointment)}
                          >
                            <div className="p-1 flex flex-col h-full">
                              <div className="flex justify-between items-center">
                                <span className="font-medium truncate">
                                  {formatTimeInTimeZone(appointment.start, 'HH:mm')}
                                </span>
                                {appointment.isManualBooking && (
                                  <Edit3 size={12} className="ml-1 flex-shrink-0" />
                                )}
                              </div>
                              <p className="font-medium truncate">{appointment.title}</p>
                              <div
                                  className={`text-xs px-1.5 py-0.5 rounded-full max-w-max ${
                                      appointment.status === 'confirmed'
                                          ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-100'
                                          : appointment.status === 'cancelled'
                                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100'
                                              : appointment.status === 'completed'
                                                  ? 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100'
                                                  : ''
                                  }`}
                              >
                                {appointment.status === 'confirmed' ? t('calendar.confirmed', 'Confirmé') :
                                    appointment.status === 'cancelled' ? t('calendar.cancelled', 'Annulé') :
                                        appointment.status === 'completed' ? t('calendar.completed', 'Terminé') : ""}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default CalendarWeekView;
